import type { CancelAutopayResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { workbenchAutoPayApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";

/**
 * Mutation hook backing the AutoPay Information panel's Cancel button. Calls
 * `WorkbenchAutoPayApi.cancelAutopay` (POST, no body), which forwards to
 * recap-service and runs caps2's Cancel AutoPay bridge (vendor cancellation,
 * contract-row write, stipulation re-check). A repeat call on an
 * already-cancelled account answers `ALREADY_CANCELLED` and is a success.
 * Self-guards against force-exit the same way useMoveAccount does, and sends a
 * per-attempt `Idempotency-Key` for correlation. On success the deal's
 * dashboard is invalidated so the AutoPay section (sourced from the dashboard
 * response) and any re-checked stipulations refetch.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useCancelAutopay(approvalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<CancelAutopayResponse> => {
      if (useForceExitStore.getState().shouldForceExit) {
        throw new Error(t("workbench.saveAbortedForceExit"));
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      const response = await workbenchAutoPayApi.cancelAutopay(approvalId, idempotencyKey);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
    },
  });
}
